/**
 * Endpoints de los **registros SUNAT** (`/api/finanzas/registros/:registro`).
 *
 * La API devuelve las filas del período, sus totales y la **definición de las
 * columnas**, así que la pantalla es la misma para el registro de compras y el de
 * ventas. Las tres salidas —el archivo PLE de SUNAT, el Excel (CSV) y el PDF— las
 * genera la API con el mismo filtro que el listado.
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { FiltrosRegistro, ListadoRegistro, TipoRegistro } from './registros-sunat.types';

export const registrosKeys = {
  todo: (registro: TipoRegistro) => ['finanzas', 'registros', registro] as const,
  lista: (registro: TipoRegistro, filtros: FiltrosRegistro) =>
    ['finanzas', 'registros', registro, 'lista', filtros] as const,
};

/** Cadena de consulta del listado y de las exportaciones. */
export function cadenaDeRegistros(filtros: FiltrosRegistro): string {
  const parametros = new URLSearchParams();

  if (filtros.periodo) parametros.set('periodo', filtros.periodo);
  if (filtros.estado !== '') parametros.set('estado', filtros.estado);
  if (filtros.tipo) parametros.set('tipo', filtros.tipo);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const registrosApi = {
  listar: (registro: TipoRegistro, filtros: FiltrosRegistro, signal?: AbortSignal) =>
    apiClient.get<ListadoRegistro>(
      `/api/finanzas/registros/${registro}${cadenaDeRegistros(filtros)}`,
      { signal },
    ),

  urlPle: (registro: TipoRegistro, filtros: FiltrosRegistro) =>
    `/api/finanzas/registros/${registro}/txt${cadenaDeRegistros(filtros)}`,

  urlExcel: (registro: TipoRegistro, filtros: FiltrosRegistro) =>
    `/api/finanzas/registros/${registro}/exportar${cadenaDeRegistros(filtros)}`,

  urlPdf: (registro: TipoRegistro, filtros: FiltrosRegistro) =>
    `/api/finanzas/registros/${registro}/pdf${cadenaDeRegistros(filtros)}`,
};

/** Listado del período con sus totales y columnas. */
export function useRegistroSunat(registro: TipoRegistro, filtros: FiltrosRegistro) {
  return useQuery({
    queryKey: registrosKeys.lista(registro, filtros),
    queryFn: ({ signal }) => registrosApi.listar(registro, filtros, signal),
    placeholderData: (anterior) => anterior,
  });
}
